import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tooltip,
  Descriptions,
  Timeline,
  Empty,
  Avatar,
  Divider,
  List,
  Steps,
  InputNumber,
  Radio,
  Checkbox,
  Switch,
  Upload,
  Row,
  Col,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  SwapOutlined,
  HistoryOutlined,
  UserSwitchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as workflowService from '../../services/workflowService';
import dayjs from 'dayjs';

const { TextArea } = Input;

// 表单模板接口
interface FormTemplate {
  id: string;
  name: string;
  key: string;
  fields: any[];
  status: number;
}

// 表单字段接口
interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: { label: string; value: string }[];
  span?: number;
}

interface ProcessNode {
  id: string;
  name: string;
  type: string;
  formType?: 'template' | 'route';
  formTemplateId?: string;
  formRoute?: string;
}

// 表单预览组件
const FormPreview: React.FC<{ formData: Record<string, any>; variables?: string }> = ({ formData, variables }) => {
  const [fields, setFields] = useState<FormField[]>([]);

  useEffect(() => {
    // 优先从variables中获取字段配置
    if (variables) {
      try {
        const vars = JSON.parse(variables);
        if (vars._formFields && Array.isArray(vars._formFields)) {
          setFields(vars._formFields);
          return;
        }
      } catch {}
    }
    // 如果没有字段配置，根据formData生成默认字段
    const defaultFields: FormField[] = Object.keys(formData).map(key => ({
      id: key,
      name: key,
      label: key,
      type: typeof formData[key] === 'boolean' ? 'switch' : typeof formData[key] === 'number' ? 'number' : 'input',
      span: 12,
    }));
    setFields(defaultFields);
  }, [formData, variables]);

  const renderFieldValue = (field: FormField, value: any) => {
    const commonStyle = { width: '100%' };
    switch (field.type) {
      case 'input': return <Input value={value} disabled style={commonStyle} />;
      case 'textarea': return <TextArea value={value} disabled rows={3} style={commonStyle} />;
      case 'number': return <InputNumber value={value} disabled style={commonStyle} />;
      case 'select': return <Select value={value} disabled style={commonStyle} options={field.options} />;
      case 'date': return <Input value={value} disabled style={commonStyle} />;
      case 'dateRange': return <Input value={Array.isArray(value) ? value.join(' ~ ') : value} disabled style={commonStyle} />;
      case 'radio': return <Radio.Group value={value} disabled options={field.options} />;
      case 'checkbox': return <Checkbox.Group value={Array.isArray(value) ? value : [value]} disabled options={field.options} />;
      case 'switch': return <Switch checked={value} disabled />;
      case 'file': return <Upload disabled><Button icon={<UploadOutlined />} disabled>已上传文件</Button></Upload>;
      default: return <Input value={String(value ?? '')} disabled style={commonStyle} />;
    }
  };

  if (fields.length === 0) return <Empty description="暂无表单数据" />;

  return (
    <Form layout="vertical" style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
      <Row gutter={16}>
        {fields.map(field => (
          <Col span={field.span || 12} key={field.id}>
            <Form.Item label={field.label} style={{ marginBottom: 12 }}>
              {renderFieldValue(field, formData[field.name])}
            </Form.Item>
          </Col>
        ))}
      </Row>
    </Form>
  );
};

const WorkflowTodo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [todoTasks, setTodoTasks] = useState<workflowService.WorkflowTask[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<workflowService.WorkflowTask | null>(null);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [currentHistory, setCurrentHistory] = useState<workflowService.WorkflowHistory[]>([]);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [delegateModalVisible, setDelegateModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [currentInstance, setCurrentInstance] = useState<workflowService.WorkflowInstance | null>(null);
  const [instanceTasks, setInstanceTasks] = useState<workflowService.WorkflowTask[]>([]);
  const [currentFormData, setCurrentFormData] = useState<Record<string, any>>({});
  
  const [form] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [delegateForm] = Form.useForm();

  useEffect(() => {
    loadData();
  }, [page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await workflowService.getMyTodoTasks({ page, pageSize: 10 });
      if (res.data) {
        setTodoTasks(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (task: workflowService.WorkflowTask) => {
    setCurrentTask(task);
    form.resetFields();
    try {
      const formData = task.instance?.formData ? JSON.parse(task.instance.formData) : {};
      setCurrentFormData(formData);
    } catch {
      setCurrentFormData({});
    }
    setApproveModalVisible(true);
  };

  const submitApprove = async (action: 'approve' | 'reject') => {
    if (!currentTask) return;
    try {
      const values = await form.validateFields();
      await workflowService.approveTask(currentTask.id, {
        action,
        comment: values.comment,
        formData: currentFormData,
      });
      message.success(action === 'approve' ? '审批通过' : '已拒绝');
      setApproveModalVisible(false);
      loadData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleTransfer = (task: workflowService.WorkflowTask) => {
    setCurrentTask(task);
    transferForm.resetFields();
    setTransferModalVisible(true);
  };

  const submitTransfer = async () => {
    if (!currentTask) return;
    try {
      const values = await transferForm.validateFields();
      await workflowService.transferTask(currentTask.id, values);
      message.success('转办成功');
      setTransferModalVisible(false);
      loadData();
    } catch (error) {
      message.error('转办失败');
    }
  };

  const handleDelegate = (task: workflowService.WorkflowTask) => {
    setCurrentTask(task);
    delegateForm.resetFields();
    setDelegateModalVisible(true);
  };

  const submitDelegate = async () => {
    if (!currentTask) return;
    try {
      const values = await delegateForm.validateFields();
      await workflowService.delegateTask(currentTask.id, values);
      message.success('委托成功');
      setDelegateModalVisible(false);
      loadData();
    } catch (error) {
      message.error('委托失败');
    }
  };

  const viewHistory = async (instanceId: number) => {
    try {
      const res = await workflowService.getInstanceHistory(instanceId);
      if (res.data) {
        setCurrentHistory(res.data as workflowService.WorkflowHistory[]);
        setHistoryModalVisible(true);
      }
    } catch (error) {
      message.error('获取历史失败');
    }
  };

  const viewDetail = async (instance: workflowService.WorkflowInstance) => {
    setCurrentInstance(instance);
    try {
      const [historyRes, tasksRes] = await Promise.all([
        workflowService.getInstanceHistory(instance.id),
        workflowService.getInstanceTasks(instance.id),
      ]);
      setCurrentHistory(historyRes.data as workflowService.WorkflowHistory[] || []);
      setInstanceTasks(tasksRes.data as workflowService.WorkflowTask[] || []);
      setDetailDrawerVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const columns: ColumnsType<workflowService.WorkflowTask> = [
    {
      title: '流程标题',
      dataIndex: ['instance', 'title'],
      key: 'title',
      render: (_, record) => (
        <span style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => record.instance && viewDetail(record.instance)}>
          {record.instance?.title || '-'}
        </span>
      ),
    },
    {
      title: '流程类型',
      dataIndex: ['instance', 'definitionName'],
      key: 'definitionName',
      render: (_, record) => record.instance?.definitionName || '-',
    },
    {
      title: '当前节点',
      dataIndex: 'nodeName',
      key: 'nodeName',
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: '发起人',
      dataIndex: ['instance', 'initiatorName'],
      key: 'initiator',
      render: (_, record) => record.instance?.initiatorName || '-',
    },
    {
      title: '发起时间',
      dataIndex: ['instance', 'startTime'],
      key: 'startTime',
      render: (_, record) => record.instance?.startTime?.substring(0, 16) || '-',
    },
    {
      title: '等待时长',
      key: 'waitTime',
      render: (_, record) => {
        if (!record.createdAt) return '-';
        const hours = dayjs().diff(dayjs(record.createdAt), 'hour');
        if (hours < 24) return `${hours}小时`;
        return `${Math.floor(hours / 24)}天${hours % 24}小时`;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => handleApprove(record)}>审批</Button>
          <Tooltip title="转办"><Button size="small" icon={<SwapOutlined />} onClick={() => handleTransfer(record)} /></Tooltip>
          <Tooltip title="委托"><Button size="small" icon={<UserSwitchOutlined />} onClick={() => handleDelegate(record)} /></Tooltip>
          <Tooltip title="历史"><Button size="small" icon={<HistoryOutlined />} onClick={() => viewHistory(record.instanceId)} /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="待办任务">
        <Table
          columns={columns}
          dataSource={todoTasks}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 10,
            showTotal: (t) => `共 ${t} 条`,
            onChange: setPage,
          }}
        />
      </Card>

      {/* 审批弹窗 */}
      <Modal
        title="审批任务"
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        width={700}
        footer={[
          <Button key="cancel" onClick={() => setApproveModalVisible(false)}>取消</Button>,
          <Button key="reject" danger icon={<CloseOutlined />} onClick={() => submitApprove('reject')}>拒绝</Button>,
          <Button key="approve" type="primary" icon={<CheckOutlined />} onClick={() => submitApprove('approve')}>通过</Button>,
        ]}
      >
        <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="流程标题" span={2}>{currentTask?.instance?.title}</Descriptions.Item>
          <Descriptions.Item label="流程类型">{currentTask?.instance?.definitionName}</Descriptions.Item>
          <Descriptions.Item label="当前节点"><Tag color="blue">{currentTask?.nodeName}</Tag></Descriptions.Item>
          <Descriptions.Item label="发起人">{currentTask?.instance?.initiatorName}</Descriptions.Item>
          <Descriptions.Item label="发起时间">{currentTask?.instance?.startTime?.substring(0, 16)}</Descriptions.Item>
        </Descriptions>
        {Object.keys(currentFormData).length > 0 && (
          <>
            <Divider>表单数据</Divider>
            <FormPreview formData={currentFormData} variables={currentTask?.instance?.variables} />
          </>
        )}
        <Form form={form} layout="vertical">
          <Form.Item name="comment" label="审批意见">
            <TextArea rows={4} placeholder="请输入审批意见（可选）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 转办弹窗 */}
      <Modal title="转办任务" open={transferModalVisible} onCancel={() => setTransferModalVisible(false)} onOk={submitTransfer}>
        <Form form={transferForm} layout="vertical">
          <Form.Item name="toUserId" label="转办给" rules={[{ required: true }]}>
            <Select placeholder="选择转办人">
              <Select.Option value="1">管理员</Select.Option>
              <Select.Option value="2">张三</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="toUserName" label="转办人姓名" rules={[{ required: true }]}>
            <Input placeholder="转办人姓名" />
          </Form.Item>
          <Form.Item name="comment" label="转办说明">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 委托弹窗 */}
      <Modal title="委托任务" open={delegateModalVisible} onCancel={() => setDelegateModalVisible(false)} onOk={submitDelegate}>
        <Form form={delegateForm} layout="vertical">
          <Form.Item name="toUserId" label="委托给" rules={[{ required: true }]}>
            <Select placeholder="选择被委托人">
              <Select.Option value="1">管理员</Select.Option>
              <Select.Option value="2">张三</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="toUserName" label="被委托人姓名" rules={[{ required: true }]}>
            <Input placeholder="被委托人姓名" />
          </Form.Item>
          <Form.Item name="comment" label="委托说明">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 历史弹窗 */}
      <Modal title="审批历史" open={historyModalVisible} onCancel={() => setHistoryModalVisible(false)} footer={null} width={700}>
        {currentHistory.length > 0 ? (
          <Timeline
            items={currentHistory.map((h) => ({
              color: h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue',
              children: (
                <div>
                  <Space><Avatar size="small">{h.operatorName?.charAt(0)}</Avatar><strong>{h.operatorName}</strong><Tag>{h.nodeName}</Tag></Space>
                  <div style={{ marginTop: 8, marginLeft: 32 }}>
                    <Tag color={h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue'}>
                      {h.action === 'submit' && '发起流程'}{h.action === 'approve' && '审批通过'}{h.action === 'reject' && '审批拒绝'}
                    </Tag>
                    {h.comment && <div style={{ marginTop: 4, color: '#666', background: '#f5f5f5', padding: 8, borderRadius: 4 }}>{h.comment}</div>}
                  </div>
                </div>
              ),
            }))}
          />
        ) : <Empty description="暂无历史记录" />}
      </Modal>

      {/* 详情弹窗 */}
      <Modal title="流程详情" open={detailDrawerVisible} onCancel={() => setDetailDrawerVisible(false)} footer={null} width={800}>
        {currentInstance && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="流程标题" span={2}>{currentInstance.title}</Descriptions.Item>
              <Descriptions.Item label="流程类型">{currentInstance.definitionName}</Descriptions.Item>
              <Descriptions.Item label="状态"><Tag color={workflowService.instanceStatusMap[currentInstance.status]?.color}>{workflowService.instanceStatusMap[currentInstance.status]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="发起人">{currentInstance.initiatorName}</Descriptions.Item>
              <Descriptions.Item label="发起时间">{currentInstance.startTime?.substring(0, 16)}</Descriptions.Item>
            </Descriptions>
            <Divider>流程进度</Divider>
            <Steps orientation="vertical" size="small" current={currentHistory.length - 1}
              items={currentHistory.map((h, index) => ({
                title: <Space><span>{h.nodeName}</span><Tag color={h.action === 'approve' ? 'green' : h.action === 'reject' ? 'red' : 'blue'}>{h.action === 'submit' ? '发起' : h.action === 'approve' ? '通过' : '拒绝'}</Tag></Space>,
                description: <div>{h.operatorName} - {h.createdAt?.substring(0, 16)}{h.comment && <div style={{ color: '#666' }}>意见: {h.comment}</div>}</div>,
                status: h.action === 'reject' ? 'error' : index === currentHistory.length - 1 && currentInstance.status === 1 ? 'process' : 'finish',
              }))}
            />
            {instanceTasks.length > 0 && (
              <>
                <Divider>任务列表</Divider>
                <List size="small" dataSource={instanceTasks} renderItem={(task) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar size="small">{task.assigneeName?.charAt(0)}</Avatar>}
                      title={<Space><span>{task.nodeName}</span><Tag color={workflowService.taskStatusMap[task.status]?.color}>{workflowService.taskStatusMap[task.status]?.text}</Tag></Space>}
                      description={<div>处理人: {task.assigneeName}</div>}
                    />
                  </List.Item>
                )} />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkflowTodo;
